import React, { useMemo, useState } from "react";
import { Row, Col, Form, Select, Input, Button, Collapse, DatePicker } from "antd";
import type { FormInstance } from "antd";
import { v4 as uuid } from "uuid";
import { useAppSelector } from "../..";
import type { AnyObject, DictionaryDto } from "../../lib/interfaces";
import InputNumberFormat from "../common/InputNumberFormat";
import { checkIsLoaded, checkIsLoading, displayFormat } from "../../lib/utils";
import { IClientType, ISearchFilterField } from "../../lib/types";
import { AccountBulkFiltersToggler } from "../AccountBulkFiltersToggler/AccountBulkFiltersToggler";
import "./AccountChargesSearchFilters.less";

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

const isAdjustmentList = {
  0: "Начисление",
  1: "Корректировка",
};

const dateFormat = displayFormat;
const { RangePicker } = DatePicker;
const { Option } = Select;

interface ComponentProps {
  form: FormInstance;
  defaultSearchParams: AnyObject;
  onSearch: (values: AnyObject) => void;
  resetSearchPaymentsAndResetSetSelectedRowKeys: () => void;
  filtersCollapseState: string;
  setFiltersCollapseState: React.Dispatch<React.SetStateAction<string>>;
}

export const AccountChargesSearchFilters = (props: ComponentProps) => {
  const {
    form,
    defaultSearchParams,
    onSearch,
    resetSearchPaymentsAndResetSetSelectedRowKeys,
    filtersCollapseState,
    setFiltersCollapseState,
  } = props;

  const { rateTypeLoading, rateModesEventsLoading } = useAppSelector(
    (state) => state.rtkDictionaries
  );
  const { chargesLoading } = useAppSelector((state) => state.charges);
  const { tenant } = useAppSelector((state) => state.tenants);
  const { agreements, agreementsLoading } = useAppSelector((state) => state.agreements);

  const rateModesEvents = (useAppSelector(
    (state) => state.rtkDictionaries.rateModesEvents
  ) as unknown) as DictionaryDto[];

  const rateType = (useAppSelector(
    (state) => state.rtkDictionaries.rateType
  ) as unknown) as DictionaryDto[];

  const serviceTypes = (useAppSelector(
    (state) => state.service.serviceTypes
  ) as unknown) as DictionaryDto[];

  const serviceTypesFetchingState = useAppSelector(
    (state) => state.service.serviceTypesFetchingState
  );

  const [clientType, setClientType] = useState<IClientType>("ORGANIZATION");

  const getPanelHeader = () =>
    filtersCollapseState === "unfolded" ? (
      ""
    ) : (
      <div className="bulk__panel_header">Показать фильтры</div>
    );

  const toggleFiltersCollapseState = () => {
    const newState = filtersCollapseState === "unfolded" ? "folded" : "unfolded";
    setFiltersCollapseState(newState);
  };

  const allFields: ISearchFilterField[] = useMemo(
    () => [
      {
        label: "Договоры",
        fieldElement: (
          <Form.Item label="Договоры" name="agreementId">
            <Select
              placeholder="Договор"
              allowClear
              showSearch
              filterOption={(input, option) =>
                option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {checkIsLoaded(agreementsLoading) &&
                agreements.length !== 0 &&
                agreements.map(({ id, agreementNumber: aggNumber }) => (
                  <Select.Option key={id} value={id}>
                    {aggNumber}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Номер СФ",
        fieldElement: (
          <Form.Item label="Номер СФ" name="invoiceExtNum" style={{ marginBottom: "0" }}>
            <Input allowClear />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Вид цены",
        fieldElement: (
          <Form.Item label="Вид цены" name="rateModeId" style={{ marginBottom: "0" }}>
            <Select showSearch placeholder="Выбрать" allowClear>
              {checkIsLoaded(rateModesEventsLoading) &&
                rateModesEvents &&
                rateModesEvents.map((item) => (
                  <Option key={item.id} value={item.id}>
                    {item.name}
                  </Option>
                ))}
            </Select>
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Дата СФ",
        fieldElement: (
          <Form.Item label="Дата СФ" name="invoiceDate">
            <RangePicker style={{ width: "100%" }} format={dateFormat} allowClear />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Тип продукта",
        fieldElement: (
          <Form.Item label="Тип продукта" name="serviceTypeName">
            <Select placeholder="Выбрать" allowClear>
              {checkIsLoaded(serviceTypesFetchingState) &&
                serviceTypes &&
                serviceTypes.map((item) => (
                  <Option key={item.id} value={item.name}>
                    {item.name}
                  </Option>
                ))}
            </Select>
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Номер счета",
        fieldElement: (
          <Form.Item label="Номер счета" name="billNumber">
            <Input allowClear />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Тип цены",
        fieldElement: (
          <Form.Item label="Тип цены" name="rateTypeId">
            <Select showSearch placeholder="Выбрать" allowClear>
              {checkIsLoaded(rateTypeLoading) &&
                rateType &&
                rateType.map((item) => (
                  <Option key={item.id} value={item.id}>
                    {item.name}
                  </Option>
                ))}
            </Select>
          </Form.Item>
        ),
        visible: false,
      },
      {
        label: "Сумма счета с",
        fieldElement: (
          <Form.Item label="Сумма счета с">
            <Row align="middle">
              <Col span={10}>
                <Form.Item name="billAmountFrom" style={{ marginBottom: "0" }}>
                  <InputNumberFormat allowClear decimalScale={2} />
                </Form.Item>
              </Col>
              <Col
                span={4}
                style={{ display: "flex", justifyContent: "center", alignItems: "center" }}
              >
                <span>по</span>
              </Col>
              <Col span={10}>
                <Form.Item name="billAmountTo" style={{ marginBottom: "0" }}>
                  <InputNumberFormat allowClear decimalScale={2} />
                </Form.Item>
              </Col>
            </Row>
          </Form.Item>
        ),
        visible: false,
      },
      {
        label: "Количество с",
        fieldElement: (
          <Form.Item label="Количество с">
            <Row align="middle">
              <Col span={10}>
                <Form.Item name="unitsFrom" style={{ marginBottom: "0" }}>
                  <InputNumberFormat allowClear decimalScale={0} />
                </Form.Item>
              </Col>
              <Col
                span={4}
                style={{ display: "flex", justifyContent: "center", alignItems: "center" }}
              >
                <span>по</span>
              </Col>
              <Col span={10}>
                <Form.Item name="unitsTo" style={{ marginBottom: "0" }}>
                  <InputNumberFormat allowClear decimalScale={0} />
                </Form.Item>
              </Col>
            </Row>
          </Form.Item>
        ),
        visible: false,
      },
      {
        label: "Дата счета",
        fieldElement: (
          <Form.Item label="Дата счета" name="billDate">
            <RangePicker style={{ width: "100%" }} format={dateFormat} allowClear />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Сумма начисления с",
        fieldElement: (
          <Form.Item label="Сумма начисления с">
            <Row align="middle">
              <Col span={10}>
                <Form.Item name="chargeAmountFrom" style={{ marginBottom: "0" }}>
                  <InputNumberFormat allowClear decimalScale={2} />
                </Form.Item>
              </Col>
              <Col
                span={4}
                style={{ display: "flex", justifyContent: "center", alignItems: "center" }}
              >
                <span>по</span>
              </Col>
              <Col span={10}>
                <Form.Item name="chargeAmountTo" style={{ marginBottom: "0" }}>
                  <InputNumberFormat allowClear decimalScale={2} />
                </Form.Item>
              </Col>
            </Row>
          </Form.Item>
        ),
        visible: false,
      },
      {
        label: "Период счета",
        fieldElement: (
          <Form.Item label="Период счета" name="billPeriod">
            <RangePicker style={{ width: "100%" }} format={dateFormat} allowClear />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Дата формирования начислений",
        fieldElement: (
          <Form.Item label="Дата формирования начислений" name="chargeDate">
            <RangePicker style={{ width: "100%" }} format={dateFormat} allowClear={false} />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Начисление/Корректировка",
        fieldElement: (
          <Form.Item label="Начисление/Корректировка" name="isAdjustment">
            <Select showSearch placeholder="Выбрать" allowClear>
              {isAdjustmentList &&
                Object.keys(isAdjustmentList).map((item) => (
                  <Option key={uuid()} value={item}>
                    {isAdjustmentList[item]}
                  </Option>
                ))}
            </Select>
          </Form.Item>
        ),
        visible: false,
      },
      {
        label: "Период формирования начислений",
        fieldElement: (
          <Form.Item label="Период формирования начислений" name="chargePeriod">
            <RangePicker style={{ width: "100%" }} format={dateFormat} allowClear={false} />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "ID начислений",
        fieldElement: (
          <Form.Item
            label="ID начислений"
            name="chargeIds"
            rules={[
              {
                validator: (x, value) => {
                  if (value && /[^0-9,]/.test(value)) {
                    return Promise.reject();
                  }
                  return Promise.resolve();
                },
                message: "В качестве разделителя может использоваться только запятая",
              },
            ]}
          >
            <Input allowClear placeholder={`Введите ID начислений через ","`} />
          </Form.Item>
        ),
        visible: false,
      },
      {
        label: "ID инвойса",
        fieldElement: (
          <Form.Item label="ID инвойса" name="invoiceId">
            <Input
              onKeyPress={(e) => {
                if (["-", ".", ",", "+"].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              type="number"
              allowClear
            />
          </Form.Item>
        ),
        visible: false,
      },
    ],
    [
      rateTypeLoading,
      rateType,
      isAdjustmentList,
      tenant,
      clientType,
      agreements,
      agreementsLoading,
      rateModesEvents,
      rateModesEventsLoading,
      serviceTypes,
      serviceTypesFetchingState,
    ]
  );

  const [fields, setFields] = useState<ISearchFilterField[]>(allFields);

  return (
    <div className="account-charges-search-filters">
      <div className="inner-row">
        <Collapse
          defaultActiveKey="1"
          onChange={toggleFiltersCollapseState}
          expandIconPosition="right"
          className="filter-collapse"
          style={{ marginBottom: "20px" }}
        >
          <Collapse.Panel header={getPanelHeader()} key="1">
            <Row justify="end">
              <AccountBulkFiltersToggler
                allFields={allFields}
                setFields={setFields}
                filterName="account-bulk-charges"
                disabledFieldsName={[
                  "Дата формирования начислений",
                  "Период формирования начислений",
                ]}
              />
            </Row>
            <Form
              form={form}
              labelAlign="left"
              initialValues={defaultSearchParams}
              {...layout}
              onFinish={onSearch}
            >
              <Row gutter={50}>
                {fields
                  .filter(({ visible }) => visible)
                  .map(({ fieldElement }, idx) => (
                    <Col span={12} key={idx}>
                      {fieldElement}
                    </Col>
                  ))}
              </Row>
              <Form.Item wrapperCol={{ span: 24 }} style={{ textAlign: "right", marginBottom: 0 }}>
                <Button onClick={resetSearchPaymentsAndResetSetSelectedRowKeys}>
                  Сброс параметров
                </Button>
                <Button loading={checkIsLoading(chargesLoading)} htmlType="submit" type="primary">
                  Применить
                </Button>
              </Form.Item>
            </Form>
          </Collapse.Panel>
        </Collapse>
      </div>
    </div>
  );
};
